import type { Suggestion } from '../../aiSuggestionsPluginType';

import { deriveHost } from './streaming';
/**
 * Fetches content suggested edits for the given content ARI.
 *
 * @param contentAri - The content ARI to fetch suggested edits for
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Promise resolving to the items array from the API response
 */
export async function fetchSuggestions({
	contentAri,
	contentAdf,
	generate = true,
	signal,
}: {
	contentAdf?: string;
	contentAri: string;
	generate: boolean;
	signal?: AbortSignal;
}): Promise<Suggestion[]> {
	const payload = {
		experience_id: 'ai-content-suggested-edits',
		ai_feature_input: {
			generate,
			debug: false,
			skip_ers: false,
			generator_overrides: generate
				? [
						'SUGGESTED_SECTIONS',
						'EVIDENCE_AND_ACCURACY',
						'FORMATTING_AND_REMIX',
						'WRITING_TONE',
						'POSITIONING_AND_FLOW',
						'CLARITY_AND_CONCISENESS',
						'SPELLING_AND_GRAMMAR',
						'FORMATTING_POLISH',
					]
				: [],
			...(contentAdf ? { content_adf: contentAdf, content_ari: '' } : { content_ari: contentAri }),
		},
	};

	const url = `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/content-suggested-edits`;
	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		body: JSON.stringify(payload),
		signal,
	});

	if (!response.ok) {
		throw new Error(`Content suggested edits request failed: ${response.status}`);
	}

	const data = (await response.json()) as {
		ai_feature_output?: {
			items?: Suggestion[];
		};
	};

	const items = data?.ai_feature_output?.items;
	return Array.isArray(items) ? items : [];
}

export async function updateSuggestionStatus({
	updates,
	signal,
}: {
	signal?: AbortSignal;
	updates: Array<{ id: string; status: 'ACTIONED' | 'DISMISSED' }>;
}): Promise<void> {
	// /ai-features/content-suggested-edits/update-status
	const payload = {
		experience_id: 'ai-content-suggested-edits',
		ai_feature_input: {
			updates,
		},
	};

	const url = `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/content-suggested-edits/update-status`;
	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			'X-Channel-Id': 'editor',
			'X-Product': 'confluence',
			'X-Experience-Id': 'editor',
		},
		body: JSON.stringify(payload),
		signal,
	});

	if (!response.ok) {
		throw new Error(`Content suggested edits request failed: ${response.status}`);
	}
}
